import { useEffect, useMemo, useState, type ReactNode } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Stack,
  Switch,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import { useAdminExportLoanFnmMutation } from '@/apis/extended/loanApplicationApi';
import { LoanFileAdminControls } from '@/components/Admin/AdminWorkspace/LoanFileAdminControls';
import { formatPrice } from '@/utils/helper';
import {
  agencyRatios,
  assetSummary,
  defaultScenario,
  dscrStatus,
  dtiStatus,
  experienceSummary,
  incomeSummary,
  investorMetrics,
  isInvestorFile,
  ltvStatus,
  monthlyDebts,
  occupancyOf,
  parseDate,
  RENTAL_INCOME_FACTOR,
  RETIREMENT_HAIRCUT,
  reviewFindings,
  SELLING_COST_PCT,
  type HousingExpense,
  type RatioStatus,
  type ReviewScenario,
} from '@/utils/loanReview/underwriting';
import type { LoanApplication } from '@/types/LoanApplicationType';

type Props = {
  application: LoanApplication | null;
  onClose: () => void;
};

const money = (v: number | null | undefined, digits = 0) =>
  v === null || v === undefined ? '—' : formatPrice(v, digits);
const pct = (v: number | null | undefined, digits = 1) =>
  v === null || v === undefined ? '—' : `${(v * 100).toFixed(digits)}%`;
const months = (v: number | null | undefined) =>
  v === null || v === undefined ? '—' : `${v.toFixed(1)} mo`;
const tenure = (m: number | null) =>
  m === null ? '—' : `${Math.floor(m / 12)}y ${m % 12}m`;
const dateLabel = (v?: string) => {
  const d = parseDate(v);
  return d ? d.toLocaleDateString() : '—';
};
const maskSsn = (ssn?: string) => {
  const d = String(ssn || '').replace(/\D/g, '');
  return d.length === 9 ? `***-**-${d.slice(-4)}` : '—';
};
const borrowerName = (app: LoanApplication) => {
  const b =
    app.borrowers?.find((x) => x.role === 'primary') ?? app.borrowers?.[0];
  return (
    [b?.firstName, b?.lastName].filter(Boolean).join(' ') ||
    app.applicantUser?.name ||
    'Applicant'
  );
};

function Metric({
  label,
  value,
  status = 'unknown',
  hint,
}: {
  label: string;
  value: ReactNode;
  status?: RatioStatus;
  hint?: string;
}) {
  const theme = useTheme();
  const color = {
    good: theme.palette.success.main,
    caution: theme.palette.warning.main,
    high: theme.palette.error.main,
    unknown: theme.palette.text.secondary,
  }[status];
  return (
    <Paper
      variant="outlined"
      sx={{ p: 1.5, borderLeft: `4px solid ${color}`, minWidth: 0 }}
    >
      <Typography variant="caption" color="text.secondary" noWrap>
        {label}
      </Typography>
      <Typography variant="h6" fontWeight={700} sx={{ color, lineHeight: 1.3 }}>
        {value}
      </Typography>
      {hint && (
        <Typography variant="caption" color="text.secondary">
          {hint}
        </Typography>
      )}
    </Paper>
  );
}

function SummaryRows({ rows }: { rows: [string, ReactNode][] }) {
  return (
    <Table size="small">
      <TableBody>
        {rows.map(([k, v]) => (
          <TableRow key={k}>
            <TableCell sx={{ color: 'text.secondary', border: 0, pl: 0 }}>
              {k}
            </TableCell>
            <TableCell align="right" sx={{ border: 0, pr: 0, fontWeight: 600 }}>
              {v}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

function HousingBreakdown({ h }: { h: HousingExpense }) {
  return (
    <SummaryRows
      rows={[
        ['Principal & interest', money(h.principalInterest, 2)],
        ['Property taxes', money(h.taxes, 2)],
        ['Insurance', money(h.insurance, 2)],
        ['HOA', money(h.hoa, 2)],
        ['Mortgage insurance', money(h.mortgageInsurance, 2)],
        ['Total (PITIA)', money(h.total, 2)],
      ]}
    />
  );
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <Paper variant="outlined" sx={{ p: 2, minWidth: 0 }}>
      <Typography variant="subtitle2" fontWeight={700} gutterBottom>
        {title}
      </Typography>
      {children}
    </Paper>
  );
}

export function LoanReviewModal({ application, onClose }: Props) {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const [tab, setTab] = useState('income');
  const [scenario, setScenario] = useState<ReviewScenario | null>(null);
  const [exportWarnings, setExportWarnings] = useState<string[]>([]);
  const [exportFnm, { isLoading: exporting }] = useAdminExportLoanFnmMutation();

  const appId = application?._id;
  useEffect(() => {
    if (!application) return;
    setScenario(defaultScenario(application));
    setExportWarnings([]);
    setTab('income');
    // Reset only when a different file is opened, not on every refetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appId]);

  const app = application;
  const investor = app ? isInvestorFile(app) : false;
  const s = scenario;

  const calc = useMemo(() => {
    if (!app || !s) return null;
    return {
      income: incomeSummary(app),
      assets: assetSummary(app),
      debts: monthlyDebts(app),
      agency: agencyRatios(app, s),
      investor: investorMetrics(app, s),
      experience: experienceSummary(app),
      findings: reviewFindings(app, s),
    };
  }, [app, s]);

  if (!app || !s || !calc) return null;

  const setNum = (key: keyof ReviewScenario) => (value: string) =>
    setScenario((prev) =>
      prev ? { ...prev, [key]: value === '' ? null : Number(value) } : prev
    );

  const handleExport = async () => {
    try {
      const res = await exportFnm({
        id: app._id,
        interestRatePct: s.interestRatePct ?? undefined,
        loanAmount: s.loanAmount ?? undefined,
        termMonths: s.termMonths || undefined,
        purchasePrice: s.purchasePrice ?? undefined,
      }).unwrap();
      const blob = new Blob([res.content], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = res.filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      setExportWarnings(res.warnings);
      if (res.warnings.length) {
        toast.warning(
          `1003 exported with ${res.warnings.length} gap(s) — see the list in the review.`
        );
      } else {
        toast.success('Fannie Mae 1003 (3.2) file exported');
      }
    } catch {
      toast.error('Export failed');
    }
  };

  const { income, assets, agency, experience, findings } = calc;
  const inv = calc.investor;
  const occ = occupancyOf(app);
  const decl = (app.declarations || {}) as Record<string, unknown>;

  const tabs: [string, string][] = [
    ['income', 'Income'],
    ['assets', 'Assets'],
    ['liabilities', 'Liabilities'],
    ['credit', 'Credit'],
    ...(investor
      ? ([['experience', 'Experience & project']] as [string, string][])
      : []),
    ['file', 'Borrowers & property'],
  ];

  return (
    <Dialog
      open
      onClose={onClose}
      fullWidth
      maxWidth="lg"
      fullScreen={fullScreen}
    >
      <DialogTitle sx={{ pr: 6 }}>
        <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap">
          <Typography variant="h6" fontWeight={700} sx={{ mr: 1 }}>
            {borrowerName(app)}
          </Typography>
          <Chip size="small" label={app.productType?.toUpperCase()} />
          <Chip
            size="small"
            variant="outlined"
            label={
              investor ? 'Investor' : occ === 'primary' ? 'Owner-occupied' : occ
            }
          />
          <Chip
            size="small"
            variant="outlined"
            label={app.status.replace(/_/g, ' ')}
          />
        </Stack>
        <Typography variant="body2" color="text.secondary">
          {app.property?.subjectPropertyAddress || 'No subject property'}
        </Typography>
        <IconButton
          aria-label="Close"
          onClick={onClose}
          sx={{ position: 'absolute', right: 12, top: 12 }}
        >
          <i className="fa-light fa-xmark" />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {/* Scenario — agency files carry no note rate, so the reviewer sets it */}
        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          <Typography variant="subtitle2" fontWeight={700} gutterBottom>
            Review scenario
          </Typography>
          <Box
            sx={{
              display: 'grid',
              gap: 1.5,
              gridTemplateColumns: {
                xs: '1fr 1fr',
                md: 'repeat(5, minmax(0, 1fr)) auto',
              },
              alignItems: 'center',
            }}
          >
            <TextField
              size="small"
              type="number"
              label={
                app.agencyFields?.loanPurpose?.toLowerCase().includes('refi')
                  ? 'Property value'
                  : 'Purchase price'
              }
              value={s.purchasePrice ?? ''}
              onChange={(e) => setNum('purchasePrice')(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">$</InputAdornment>
                ),
              }}
            />
            <TextField
              size="small"
              type="number"
              label="Loan amount"
              value={s.loanAmount ?? ''}
              onChange={(e) => setNum('loanAmount')(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">$</InputAdornment>
                ),
              }}
            />
            <TextField
              size="small"
              type="number"
              label="Note rate"
              value={s.interestRatePct ?? ''}
              onChange={(e) => setNum('interestRatePct')(e.target.value)}
              error={s.interestRatePct === null}
              helperText={
                s.interestRatePct === null ? 'Enter a rate' : undefined
              }
              inputProps={{ step: 0.125 }}
              InputProps={{
                endAdornment: <InputAdornment position="end">%</InputAdornment>,
              }}
            />
            <TextField
              size="small"
              select
              label="Term"
              value={s.termMonths}
              onChange={(e) => setNum('termMonths')(e.target.value)}
            >
              {Array.from(
                new Set([
                  12,
                  18,
                  24,
                  120,
                  180,
                  240,
                  300,
                  360,
                  480,
                  s.termMonths,
                ])
              )
                .sort((a, b) => a - b)
                .map((m) => (
                  <MenuItem key={m} value={m}>
                    {m % 12 === 0 && m >= 120 ? `${m / 12} yr` : `${m} mo`}
                  </MenuItem>
                ))}
            </TextField>
            <TextField
              size="small"
              type="number"
              label="Closing costs"
              value={s.closingCostPct}
              onChange={(e) =>
                setScenario({
                  ...s,
                  closingCostPct: Number(e.target.value) || 0,
                })
              }
              InputProps={{
                endAdornment: <InputAdornment position="end">%</InputAdornment>,
              }}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={s.interestOnly}
                  onChange={(e) =>
                    setScenario({ ...s, interestOnly: e.target.checked })
                  }
                />
              }
              label="Interest-only"
            />
          </Box>
        </Paper>

        {/* Key ratios */}
        <Box
          sx={{
            display: 'grid',
            gap: 1.5,
            mb: 2,
            gridTemplateColumns: {
              xs: '1fr 1fr',
              md: 'repeat(6, minmax(0, 1fr))',
            },
          }}
        >
          {investor ? (
            <>
              <Metric
                label="DSCR (rent / PITIA)"
                value={inv.dscr === null ? '—' : inv.dscr.toFixed(2)}
                status={dscrStatus(inv.dscr)}
                hint="≥ 1.25 strong · < 1.00 short"
              />
              <Metric
                label="LTV"
                value={pct(inv.ltv)}
                status={ltvStatus(inv.ltv, true)}
              />
              <Metric
                label="LTC"
                value={pct(inv.ltc)}
                status={ltvStatus(inv.ltc, true)}
                hint="Loan / (price + rehab)"
              />
              <Metric
                label="ARV-LTV"
                value={pct(inv.arvLtv)}
                status={ltvStatus(inv.arvLtv, true)}
              />
              <Metric
                label="Reserves"
                value={months(inv.reservesMonths)}
                status={
                  inv.reservesMonths === null
                    ? 'unknown'
                    : inv.reservesMonths >= 6
                    ? 'good'
                    : inv.reservesMonths >= 0
                    ? 'caution'
                    : 'high'
                }
                hint="After cash to close"
              />
              <Metric
                label="Experience"
                value={experience.tier}
                status={
                  experience.tier === 'Tier 1'
                    ? 'good'
                    : experience.tier === 'No experience'
                    ? 'high'
                    : 'caution'
                }
                hint={experience.tierLabel}
              />
            </>
          ) : (
            <>
              <Metric
                label="Front-end DTI"
                value={occ === 'investment' ? 'n/a' : pct(agency.frontDti)}
                status={
                  occ === 'investment'
                    ? 'unknown'
                    : dtiStatus(agency.frontDti, 'front')
                }
                hint={
                  occ === 'investment'
                    ? 'Investment property'
                    : 'Housing / income · ≤ 28%'
                }
              />
              <Metric
                label="Back-end DTI"
                value={pct(agency.backDti)}
                status={dtiStatus(agency.backDti, 'back')}
                hint="All debts / income · ≤ 36–45%"
              />
              <Metric
                label="LTV"
                value={pct(agency.ltv)}
                status={ltvStatus(agency.ltv, false)}
              />
              <Metric
                label="PITIA"
                value={money(agency.housing.total)}
                hint="Proposed housing"
              />
              <Metric
                label="Qualifying income"
                value={money(agency.qualifyingIncome)}
                hint="Monthly gross"
              />
              <Metric
                label="Reserves"
                value={months(agency.reservesMonths)}
                status={
                  agency.reservesMonths === null
                    ? 'unknown'
                    : agency.reservesMonths >= 2
                    ? 'good'
                    : agency.reservesMonths >= 0
                    ? 'caution'
                    : 'high'
                }
                hint="After cash to close"
              />
            </>
          )}
        </Box>

        {findings.length > 0 && (
          <Stack gap={0.75} sx={{ mb: 2 }}>
            {findings.map((f, i) => (
              <Alert key={i} severity={f.severity} sx={{ py: 0 }}>
                {f.message}
              </Alert>
            ))}
          </Stack>
        )}
        {exportWarnings.length > 0 && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="body2" fontWeight={600}>
              Last 1003 export was missing:
            </Typography>
            <Box component="ul" sx={{ m: 0, pl: 2 }}>
              {exportWarnings.map((w) => (
                <li key={w}>
                  <Typography variant="body2">{w}</Typography>
                </li>
              ))}
            </Box>
          </Alert>
        )}

        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          variant="scrollable"
          allowScrollButtonsMobile
          sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
        >
          {tabs.map(([key, label]) => (
            <Tab key={key} value={key} label={label} />
          ))}
        </Tabs>

        {tab === 'income' && (
          <Stack gap={2}>
            <Paper variant="outlined" sx={{ overflowX: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Employer</TableCell>
                    <TableCell>Position</TableCell>
                    <TableCell>Start</TableCell>
                    <TableCell>Tenure</TableCell>
                    <TableCell align="right">Base</TableCell>
                    <TableCell align="right">Overtime</TableCell>
                    <TableCell align="right">Bonus</TableCell>
                    <TableCell align="right">Monthly</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {income.rows.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={8}>No employment entered.</TableCell>
                    </TableRow>
                  )}
                  {income.rows.map((r, i) => (
                    <TableRow key={i} sx={{ opacity: r.isCurrent ? 1 : 0.6 }}>
                      <TableCell>
                        {r.employerName}
                        {!r.isCurrent && (
                          <Chip size="small" label="Prior" sx={{ ml: 1 }} />
                        )}
                      </TableCell>
                      <TableCell>{r.jobTitle || '—'}</TableCell>
                      <TableCell>{dateLabel(r.startDate)}</TableCell>
                      <TableCell>{tenure(r.tenureMonths)}</TableCell>
                      <TableCell align="right">{money(r.base)}</TableCell>
                      <TableCell align="right">{money(r.overtime)}</TableCell>
                      <TableCell align="right">{money(r.bonus)}</TableCell>
                      <TableCell align="right">{money(r.total)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Paper>
            <Box
              sx={{
                display: 'grid',
                gap: 2,
                gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
              }}
            >
              <Section title="Qualifying income (monthly)">
                <SummaryRows
                  rows={[
                    ['Base (current jobs)', money(income.base)],
                    ['Overtime + bonus', money(income.variable)],
                    ...(agency.subjectNetRental !== null &&
                    agency.subjectNetRental > 0
                      ? ([
                          [
                            `Subject net rental (${
                              RENTAL_INCOME_FACTOR * 100
                            }% of rent − PITIA)`,
                            money(agency.subjectNetRental),
                          ],
                        ] as [string, ReactNode][])
                      : []),
                    ['Total qualifying', money(agency.qualifyingIncome)],
                    ['Employment history', tenure(income.historyMonths)],
                    ['Self-employed', income.selfEmployed ? 'Yes' : 'No'],
                  ]}
                />
              </Section>
              <Section title="Proposed housing">
                <HousingBreakdown h={investor ? inv.housing : agency.housing} />
              </Section>
            </Box>
            {investor && (
              <Alert severity="info">
                Investor files qualify on the property — see DSCR and the
                Experience & project tab. Personal income is shown for
                reference.
              </Alert>
            )}
          </Stack>
        )}

        {tab === 'assets' && (
          <Stack gap={2}>
            <Paper variant="outlined" sx={{ overflowX: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Institution</TableCell>
                    <TableCell>Account type</TableCell>
                    <TableCell>Category</TableCell>
                    <TableCell align="right">Balance</TableCell>
                    <TableCell align="right">Counted</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {assets.rows.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={5}>No assets entered.</TableCell>
                    </TableRow>
                  )}
                  {assets.rows.map((a, i) => (
                    <TableRow key={a._id || i}>
                      <TableCell>{a.institutionName || '—'}</TableCell>
                      <TableCell>{a.accountType || '—'}</TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          variant="outlined"
                          label={a.category}
                        />
                      </TableCell>
                      <TableCell align="right">{money(a.balance)}</TableCell>
                      <TableCell align="right">{money(a.usable)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Paper>
            <Box
              sx={{
                display: 'grid',
                gap: 2,
                gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
              }}
            >
              <Section title="Asset totals">
                <SummaryRows
                  rows={[
                    ['Liquid', money(assets.byCategory.liquid)],
                    [
                      `Retirement (counted at ${RETIREMENT_HAIRCUT * 100}%)`,
                      money(assets.byCategory.retirement),
                    ],
                    ['Gift', money(assets.byCategory.gift)],
                    ['Other', money(assets.byCategory.other)],
                    ['Total entered', money(assets.total)],
                    ['Counted toward liquidity', money(assets.usable)],
                  ]}
                />
              </Section>
              <Section title="Funds to close & reserves">
                <SummaryRows
                  rows={
                    investor
                      ? [
                          [
                            'Est. cash to close (incl. unfunded rehab)',
                            money(inv.cashToClose),
                          ],
                          [
                            'Remaining after close',
                            money(
                              inv.cashToClose === null
                                ? null
                                : assets.usable - inv.cashToClose
                            ),
                          ],
                          [
                            'Reserves (months of PITIA)',
                            months(inv.reservesMonths),
                          ],
                        ]
                      : [
                          ['Down payment', money(agency.downPayment)],
                          [
                            `Est. closing costs (${s.closingCostPct}%)`,
                            money(
                              agency.cashToClose !== null &&
                                agency.downPayment !== null
                                ? agency.cashToClose - agency.downPayment
                                : agency.cashToClose
                            ),
                          ],
                          ['Est. cash to close', money(agency.cashToClose)],
                          [
                            'Remaining after close',
                            money(
                              agency.cashToClose === null
                                ? null
                                : assets.usable - agency.cashToClose
                            ),
                          ],
                          [
                            'Reserves (months of PITIA)',
                            months(agency.reservesMonths),
                          ],
                        ]
                  }
                />
              </Section>
            </Box>
          </Stack>
        )}

        {tab === 'liabilities' && (
          <Stack gap={2}>
            <Paper variant="outlined" sx={{ overflowX: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Creditor</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell align="right">Monthly</TableCell>
                    <TableCell align="right">Balance</TableCell>
                    <TableCell>In DTI</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(app.liabilityLines || []).length === 0 && (
                    <TableRow>
                      <TableCell colSpan={5}>No liabilities entered.</TableCell>
                    </TableRow>
                  )}
                  {(app.liabilityLines || []).map((l, i) => (
                    <TableRow key={l._id || i}>
                      <TableCell>{l.creditorName || '—'}</TableCell>
                      <TableCell>{l.accountType || '—'}</TableCell>
                      <TableCell align="right">
                        {money(l.monthlyPayment)}
                      </TableCell>
                      <TableCell align="right">
                        {money(l.unpaidBalance)}
                      </TableCell>
                      <TableCell>
                        {l.toBePaidOff ? (
                          <Chip size="small" label="Paid at closing" />
                        ) : (
                          'Yes'
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Paper>
            <Section title="Debt summary (monthly)">
              <SummaryRows
                rows={[
                  ['Consumer debts in DTI', money(calc.debts)],
                  ...(occ === 'investment'
                    ? agency.subjectNetRental !== null &&
                      agency.subjectNetRental < 0
                      ? ([
                          [
                            'Subject negative net rental',
                            money(-agency.subjectNetRental),
                          ],
                        ] as [string, ReactNode][])
                      : []
                    : ([
                        [
                          'Proposed housing (PITIA)',
                          money(agency.housing.total),
                        ],
                      ] as [string, ReactNode][])),
                  [
                    'Total debts for back-end DTI',
                    money(agency.qualifyingDebts),
                  ],
                ]}
              />
            </Section>
            <Alert severity="info">
              Liabilities are borrower-entered. Once credit is connected, the
              tradelines from the report will be reconciled here.
            </Alert>
          </Stack>
        )}

        {tab === 'credit' && (
          <Stack gap={2}>
            <Alert severity="info" icon={<i className="fa-light fa-plug" />}>
              Credit report integration is not connected yet. When it is, this
              tab will show the tri-merge scores, tradelines, inquiries and
              public records, and the reconciled liabilities will feed the DTI
              above.
            </Alert>
            <Box
              sx={{
                display: 'grid',
                gap: 2,
                gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
              }}
            >
              <Section title="Borrower-reported">
                <SummaryRows
                  rows={[
                    [
                      'Estimated credit score',
                      decl.estimatedCreditScore
                        ? String(decl.estimatedCreditScore)
                        : '—',
                    ],
                    [
                      'Mortgage lates (12 mo)',
                      String(decl.mortgageLates || '—'),
                    ],
                    [
                      'Months of reserves (stated)',
                      decl.monthsReserve ? String(decl.monthsReserve) : '—',
                    ],
                  ]}
                />
              </Section>
              <Section title="Declarations">
                <SummaryRows
                  rows={(
                    [
                      ['Bankruptcy', 'bankruptcy'],
                      ['Foreclosure', 'foreclosure'],
                      ['Deed in lieu', 'deedInLieu'],
                      ['Short sale', 'shortSale'],
                      ['Judgment / lien', 'judgmentLien'],
                      ['Party to lawsuit', 'partyToLawsuit'],
                      ['Borrowed down payment', 'borrowedDownPayment'],
                      ['Gift down payment', 'giftDownPayment'],
                    ] as const
                  ).map(([label, key]) => {
                    const v = String(decl[key] || '—');
                    return [
                      label,
                      v === 'Yes' ? (
                        <Chip size="small" color="warning" label="Yes" />
                      ) : (
                        v
                      ),
                    ] as [string, ReactNode];
                  })}
                />
              </Section>
            </Box>
          </Stack>
        )}

        {tab === 'experience' && investor && (
          <Stack gap={2}>
            <Box
              sx={{
                display: 'grid',
                gap: 2,
                gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
              }}
            >
              <Section title={`Experience — ${experience.tier}`}>
                <SummaryRows
                  rows={[
                    ['Exits in last 36 months', String(experience.recentExits)],
                    [
                      'Rentals owned (REO schedule)',
                      String(experience.rentalsOwned),
                    ],
                    [
                      'Completed projects (all time)',
                      String(experience.completedProjects),
                    ],
                    ['Projects still held', String(experience.heldProjects)],
                    ['Avg. profit per exit', money(experience.avgProfit)],
                    [
                      'Avg. ROI per exit',
                      experience.avgRoiPct === null
                        ? '—'
                        : `${experience.avgRoiPct.toFixed(1)}%`,
                    ],
                    [
                      'Avg. hold',
                      experience.avgHoldMonths === null
                        ? '—'
                        : `${experience.avgHoldMonths.toFixed(1)} mo`,
                    ],
                  ]}
                />
              </Section>
              <Section title="This project">
                <SummaryRows
                  rows={[
                    ['Purchase price', money(s.purchasePrice)],
                    ['Rehab budget', money(inv.rehabBudget)],
                    ['After-repair value', money(inv.arv)],
                    ['Monthly rent', money(inv.monthlyRent)],
                    [
                      'Hold period',
                      inv.holdMonths === null ? '—' : `${inv.holdMonths} mo`,
                    ],
                    [
                      'Exit strategy',
                      app.investorProgramFields?.exitStrategy || '—',
                    ],
                    ['Carrying cost over hold', money(inv.carryingCost)],
                    [
                      `Projected profit (after ${SELLING_COST_PCT}% selling)`,
                      money(inv.projectedProfit),
                    ],
                    [
                      'Projected ROI',
                      inv.projectedRoiPct === null
                        ? '—'
                        : `${inv.projectedRoiPct.toFixed(1)}%`,
                    ],
                    [
                      'DSCR (NOI method, borrower calc)',
                      inv.dscrNoi === null ? '—' : inv.dscrNoi.toFixed(2),
                    ],
                  ]}
                />
              </Section>
            </Box>
            <Paper variant="outlined" sx={{ overflowX: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Track record</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Bought</TableCell>
                    <TableCell align="right">Price</TableCell>
                    <TableCell align="right">Rehab</TableCell>
                    <TableCell>Sold</TableCell>
                    <TableCell align="right">Sale</TableCell>
                    <TableCell align="right">Profit</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(app.trackRecord || []).length === 0 && (
                    <TableRow>
                      <TableCell colSpan={8}>
                        No track record entered.
                      </TableCell>
                    </TableRow>
                  )}
                  {(app.trackRecord || []).map((t, i) => {
                    const sold = Boolean(t.salesDate && t.salesPrice);
                    return (
                      <TableRow key={t._id || i}>
                        <TableCell>{t.propertyAddress || '—'}</TableCell>
                        <TableCell>{t.propertyType || '—'}</TableCell>
                        <TableCell>{dateLabel(t.purchaseDate)}</TableCell>
                        <TableCell align="right">
                          {money(t.purchasePrice)}
                        </TableCell>
                        <TableCell align="right">
                          {money(t.renovationBudget)}
                        </TableCell>
                        <TableCell>
                          {sold ? dateLabel(t.salesDate) : 'Held'}
                        </TableCell>
                        <TableCell align="right">
                          {sold ? money(t.salesPrice) : '—'}
                        </TableCell>
                        <TableCell align="right">
                          {sold
                            ? money(
                                (t.salesPrice || 0) -
                                  (t.purchasePrice || 0) -
                                  (t.renovationBudget || 0)
                              )
                            : '—'}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Paper>
          </Stack>
        )}

        {tab === 'file' && (
          <Box
            sx={{
              display: 'grid',
              gap: 2,
              gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
            }}
          >
            {(app.borrowers || []).map((b, i) => (
              <Section
                key={i}
                title={
                  b.role === 'primary' ? 'Primary borrower' : 'Co-borrower'
                }
              >
                <SummaryRows
                  rows={[
                    [
                      'Name',
                      [b.firstName, b.middleName, b.lastName]
                        .filter(Boolean)
                        .join(' ') || '—',
                    ],
                    ...(b.hasEntityTitle && b.entityName
                      ? ([
                          [
                            'Vesting entity',
                            `${b.entityName}${
                              b.entityType ? ` (${b.entityType})` : ''
                            }`,
                          ],
                        ] as [string, ReactNode][])
                      : []),
                    ['SSN', maskSsn(b.ssn)],
                    ['Date of birth', dateLabel(b.dob)],
                    ['Email', b.email || '—'],
                    ['Phone', b.phone || '—'],
                    ['Marital status', b.maritalStatus || '—'],
                    ['Citizenship', b.citizenship || '—'],
                    ['Present address', b.presentAddress || '—'],
                    [
                      'Own / rent',
                      [
                        b.ownership,
                        b.ownedRentedYears && `${b.ownedRentedYears} yrs`,
                      ]
                        .filter(Boolean)
                        .join(' · ') || '—',
                    ],
                  ]}
                />
              </Section>
            ))}
            <Section title="Subject property">
              <SummaryRows
                rows={[
                  ['Address', app.property?.subjectPropertyAddress || '—'],
                  ['Type', app.property?.propertyType || '—'],
                  [
                    'Units',
                    app.property?.numberOfUnits
                      ? String(app.property.numberOfUnits)
                      : '—',
                  ],
                  [
                    'Year built',
                    app.property?.yearBuilt
                      ? String(app.property.yearBuilt)
                      : '—',
                  ],
                  ['Occupancy', occ],
                  [
                    'Loan purpose',
                    app.agencyFields?.loanPurpose ||
                      (app.flowProfile === 'refinance'
                        ? 'Refinance'
                        : 'Purchase'),
                  ],
                  ['Proposed rent', money(app.property?.proposedMonthlyRent)],
                ]}
              />
            </Section>
          </Box>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          px: 3,
          py: 1.5,
          gap: 1,
          flexWrap: 'wrap',
          justifyContent: 'space-between',
        }}
      >
        <LoanFileAdminControls
          app={app}
          compact
          approvalBlocker={
            s.interestRatePct === null || !(s.interestRatePct > 0)
              ? 'Enter a note rate above before approving'
              : undefined
          }
        />
        <Stack direction="row" gap={1}>
          <Button onClick={() => navigate(`/admin/loan-pipeline/${app._id}`)}>
            Open full file
          </Button>
          <Tooltip title="Fannie Mae 1003 v3.2 Loan Data File (.fnm) — imports into DU and most LOS platforms. Uses the scenario above.">
            <span>
              <Button
                variant="contained"
                onClick={handleExport}
                disabled={exporting}
                startIcon={<i className="fa-light fa-file-export" />}
              >
                Export 1003 (.fnm)
              </Button>
            </span>
          </Tooltip>
        </Stack>
      </DialogActions>
    </Dialog>
  );
}
